import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Paper, Alert, Loader, Center, Tabs, Container } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';
import intraService from 'services/intraService';
import { Contact, LanBooking, LanRegistration, LanSettingsPayload } from 'types/intra';
import { toErrorMessage, isDone, OperationKey } from './intraUtils';
import IntraHeader from './IntraHeader';
import IntraStats from './IntraStats';
import RegistrationsTable from './RegistrationsTable';
import BookingsTable from './BookingsTable';
import LanSettingsForm from './LanSettingsForm';
import DemoArchiveSection from './DemoArchiveSection';
import LocationsSection from './LocationsSection';
import SeatAssignModal from './SeatAssignModal';
import '../../assets/mantine.scss';

/**
 * IntraView is the main admin dashboard component for LAN event organizers.
 *
 * Provides a tabbed interface for managing:
 * - Event registrations (participants)
 * - Table bookings and billing status
 * - Seat blocking and manual provisioning
 * - LAN event settings (dates, capacity, features)
 * - CS2 demo archive management
 *
 * Requires authentication via Keycloak. Displays real-time statistics
 * and supports CRUD operations on all managed entities.
 *
 * @returns The complete intra admin view with header, stats, and tabbed content
 */
const IntraView: React.FC = () => {
    const { user, logout, accessToken } = useAuth();
    const [registrations, setRegistrations] = useState<LanRegistration[]>([]);
    const [bookings, setBookings] = useState<LanBooking[]>([]);
    const [lanSettings, setLanSettings] = useState<LanSettingsPayload | null>(null);
    const [draftSettings, setDraftSettings] = useState<LanSettingsPayload | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [operation, setOperation] = useState<OperationKey>(null);
    const [error, setError] = useState<string | null>(null);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
    const [provisionBooking, setProvisionBooking] = useState<LanBooking | null>(null);
    const isMobile = useMediaQuery('(max-width: 767px)');

    const isOperating = useCallback((key: string) => operation === key, [operation]);

    const withOperation = useCallback(async <T,>(key: string, task: () => Promise<T>) => {
        setOperation(key);
        try {
            const result = await task();
            setError(null);
            return result;
        } catch (err) {
            setError(toErrorMessage(err));
            throw err;
        } finally {
            setOperation(null);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const token = accessToken ?? undefined;
            const [regData, bookingData, settingsData] = await Promise.all([
                intraService.fetchRegistrations(token),
                intraService.fetchBookings(token),
                intraService.fetchLanSettings(token)
            ]);
            setRegistrations(regData);
            setBookings(bookingData);
            setLanSettings(settingsData);
            setDraftSettings(settingsData ?? null);
            setError(null);
        } catch (err) {
            setError(toErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (lanSettings) {
            setDraftSettings(lanSettings);
        }
    }, [lanSettings]);

    const stats = useMemo(() => {
        const active = registrations.filter(registration => !registration.history || registration.history === 0).length;
        const archived = registrations.length - active;
        const meals = registrations.filter(registration => registration.food).length;
        const pendingInvoices = bookings.filter(booking => !isDone(booking.done)).length;
        const pendingInvites = bookings.filter(booking => !booking.invitationSent).length;

        const statsArray = [
            { label: 'Aktiivisia ilmoittautumisia', value: active },
            { label: 'Arkistoituja', value: archived }
        ];

        if (lanSettings?.foodEnabled !== false) {
            statsArray.push({ label: 'Ruokailijoita', value: meals });
        }

        statsArray.push(
            { label: 'Avoimia laskutuksia', value: pendingInvoices },
            { label: 'Lähettämättömiä kutsuja', value: pendingInvites }
        );

        return statsArray;
    }, [bookings, registrations, lanSettings]);

    const handleHistoryChange = async (id: string, historyValue: number) => {
        await withOperation(`registration-${id}`, async () => {
            const updated = await intraService.updateRegistration(
                id,
                { history: historyValue },
                accessToken ?? undefined
            );
            setRegistrations(prev => prev.map(registration => (registration.id === id ? updated : registration)));
        });
    };

    const handleInvitationToggle = async (id: string, invitationSent: boolean) => {
        await withOperation(`booking-invite-${id}`, async () => {
            const updated = await intraService.updateBooking(id, { invitationSent }, accessToken ?? undefined);
            setBookings(prev => prev.map(booking => (booking.id === id ? updated : booking)));
        });
    };

    const handleBillingToggle = async (id: string, done: boolean) => {
        await withOperation(`booking-billing-${id}`, async () => {
            const updated = await intraService.updateBillingStatus(id, done ? 1 : 0, accessToken ?? undefined);
            setBookings(prev => prev.map(booking => (booking.id === id ? updated : booking)));
        });
    };

    const applyBookingUpdates = (updated: LanBooking[]) => {
        setBookings(prev =>
            prev.map(booking => updated.find(item => item.id === booking.id) ?? booking)
        );
        setProvisionBooking(current =>
            current ? (updated.find(item => item.id === current.id) ?? current) : current
        );
    };

    const handleAssignLocation = async (id: string, location: string | null) => {
        await withOperation(`booking-location-${id}`, async () => {
            const updated = await intraService.updateBooking(id, { location }, accessToken ?? undefined);
            applyBookingUpdates([updated]);
        });
    };

    const handleSwapLocation = async (id: string, location: string) => {
        await withOperation(`booking-location-${id}`, async () => {
            const result = await intraService.swapBookingLocations(id, location, accessToken ?? undefined);
            applyBookingUpdates([result.source, result.target].filter((item): item is LanBooking => Boolean(item)));
        });
    };

    const handleSettingsField = useCallback((field: keyof LanSettingsPayload, value: string | number | boolean | Contact[]) => {
        setDraftSettings(prev => (prev ? { ...prev, [field]: value } : prev));
    }, []);

    const handleSettingsSubmit = async () => {
        if (!draftSettings) {
            return;
        }
        await withOperation('settings', async () => {
            const updated = await intraService.updateLanSettings(draftSettings, accessToken ?? undefined);
            setLanSettings(updated);
            setDraftSettings(updated);
        });
    };

    const handleTabChange = (activeIndex: number) => {
        setActiveTabIndex(activeIndex);
    };

    return (
        <Container className="intra-view" style={{ padding: '0.5rem', width: '100%', maxWidth: '100%', boxSizing: 'border-box', marginInline: 0 }}>
            <IntraHeader
                user={user}
                loading={loading}
                onRefresh={loadData}
                onLogout={logout}
            />
            {error && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Virhe datan käsittelyssä" mb="md">
                    {error}
                </Alert>
            )}
            {loading ? (
                <Paper shadow="sm" p="xl" radius="md" withBorder>
                    <Center>
                        <Loader size="sm">Ladataan intra-dataa...</Loader>
                    </Center>
                </Paper>
            ) : (
                <>
                    <IntraStats
                        stats={stats}
                        announcement={lanSettings?.announcement}
                    />
                    <div className={isMobile ? 'mobile-tabs-container' : ''}>
                        <Tabs 
                            value={String(activeTabIndex)} 
                            variant='pills'
                            autoContrast={true}
                            color='violet.5' 
                            orientation={isMobile ? 'vertical' : 'horizontal'} 
                            onChange={(value) => handleTabChange(Number(value))}
                        >
                            <Tabs.List className={isMobile ? 'mobile-tabs-list' : ''} style={{ backgroundColor: 'white', borderRadius: '4px' }}>
                                <Tabs.Tab value="0">Ilmoittautumiset</Tabs.Tab>
                                <Tabs.Tab value="1">Pöytävaraukset</Tabs.Tab>
                                <Tabs.Tab value="2">Paikat</Tabs.Tab>
                                <Tabs.Tab value="3">LAN-asetukset</Tabs.Tab>
                                <Tabs.Tab value="4">CS2-demoarkisto</Tabs.Tab>
                            </Tabs.List>
                            <Tabs.Panel value="0" pt="md">
                                <RegistrationsTable
                                    registrations={registrations}
                                    bookings={bookings}
                                    lanSettings={lanSettings}
                                    isOperating={isOperating}
                                    onHistoryChange={handleHistoryChange}
                                    onProvisionClick={setProvisionBooking}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="1" pt="md">
                                <BookingsTable
                                    bookings={bookings}
                                    isOperating={isOperating}
                                    onInvitationToggle={handleInvitationToggle}
                                    onBillingToggle={handleBillingToggle}
                                    onProvisionClick={setProvisionBooking}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="2" pt="md">
                                <LocationsSection
                                    accessToken={accessToken}
                                    registrations={registrations}
                                    bookings={bookings}
                                    isOperating={isOperating}
                                    onAssign={handleAssignLocation}
                                    onSwap={handleSwapLocation}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="3" pt="md">
                                <LanSettingsForm
                                    draftSettings={draftSettings}
                                    lanSettings={lanSettings}
                                    isOperating={isOperating}
                                    onFieldChange={handleSettingsField}
                                    onSubmit={handleSettingsSubmit}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="4" pt="md">
                                <DemoArchiveSection accessToken={accessToken} />
                            </Tabs.Panel>
                        </Tabs>
                    </div>
                    <SeatAssignModal
                        opened={Boolean(provisionBooking)}
                        booking={provisionBooking}
                        bookings={bookings}
                        registrations={registrations}
                        accessToken={accessToken}
                        isOperating={isOperating}
                        onClose={() => setProvisionBooking(null)}
                        onAssign={handleAssignLocation}
                        onSwap={handleSwapLocation}
                    />
                </>
            )}
        </Container>
    );
};

export default IntraView;
